import { CustomFieldsService } from './../../../services/custom-fields.service';
import { Component, Input, Output, ElementRef, EventEmitter, ViewChild, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { Filter, FilterConfig, FilterSetService } from '../../../services/filter-set.service';
import { BbFacetListOption } from '../bb-facet-list/bb-facet-list.component';

import { BbDatepickerComponent } from '../bb-datepicker/bb-datepicker.component';

import dayjs from 'dayjs';

@Component({
	selector: 'bb-horizontal-filter',
	templateUrl: './bb-horizontal-filter.component.html',
	styleUrls: ['./bb-horizontal-filter.component.scss']
})

export class BbHorizontalFilterComponent implements OnInit, OnDestroy {
	@Input() filter: Filter;
	@Output() onRemove: EventEmitter<Filter> = new EventEmitter<Filter>();

	@ViewChild('facetList') facetList: ElementRef | any;
	@ViewChild('datePicker') datePicker: BbDatepickerComponent | any;
	@ViewChild('textSearchInput') textSearchInput: ElementRef | any;
	@ViewChild('dateInputSelection') dateInputSelection: ElementRef | any;

	public filterConfig: FilterConfig;
	public internalFilter: Filter;
	facetsLoading = false;

	// private resource: string;
	options: BbFacetListOption[];
	private dateOption: string;
	private date: Date;
	private editing = false;
	private activeFilterChangedSubscription: Subscription;
	private textSearchValue: string;
	// private localeFormat: string;
	valueLabelMap: any = {};

	constructor(
		private filterSetService: FilterSetService,
		private customFieldsService: CustomFieldsService,
		private ref: ChangeDetectorRef) {

		// this.resource = 'MediaClip';
		this.dateOption = 'BEFORE';
	}

	ngOnInit() {
		this.getValueMap(this.filter.field);
		this.date = null;
		this.filterConfig = this.filterSetService.getFilterConfigForProperty(this.filter.field);
		if (this.filterConfig && this.filterConfig.type === 'date' && (this.filter.value.length === 0 || !this.filter.value[0])) {
			this.filter.operator = 'isBefore';
		}
		this.setInternalFilter(this.filter);

		if (this.filterConfig) {
			if (this.filterConfig.type === 'text') {
				if (this.internalFilter.value.length > 0) {
					this.textSearchValue = this.internalFilter.value[0];
				}
			} else if (this.filterConfig.type === 'date') {
				if (this.internalFilter.value.length > 0) {
					this.date = new Date(this.getFromDate(this.internalFilter.value[0]));
				} else {
					this.date = dayjs().startOf('day').toDate();
				}
				switch (this.internalFilter.operator) {
					case 'isBefore':
						this.dateOption = 'BEFORE';
						break;
					case 'isAfter':
						this.dateOption = 'AFTER';
						break;
					default:
						this.dateOption = 'ON';
				}
			}
		}

		setTimeout(() => {
			this.filterSetService.updateFilter(this.internalFilter, this.filter);
		});

		if (this.filterSetService.getActiveFilter() === this.filter) {
			this.editing = true;
			if (this.editing) {
				this.determineOptions();
			}
		}

		this.activeFilterChangedSubscription = this.filterSetService.activeFilterChangedObservable.subscribe(activeFilter => {
			this.editing = (JSON.stringify(activeFilter) === JSON.stringify(this.filter));
			this.ref.detectChanges();
			if (this.editing) {
				this.determineOptions();
			}
		});

		// this.showDropdown = true;
		this.focusInput();
	}

	getValueMap(fieldName) {
		const customField = this.customFieldsService.getCustomField('mediaclip', fieldName);
		if (customField?.options?.length) {
			customField.options.forEach(option => {
				this.valueLabelMap[option.value] = option.label;
			});
		}
	}

	updateDate(date: Date) {
		this.date = date;
		this.internalFilter.value = [date.toISOString()];
	}

	ngOnDestroy() {
		if (this.activeFilterChangedSubscription) {
			this.activeFilterChangedSubscription.unsubscribe();
		}
	}

	setCompareOption(compareOption: string) {
		this.filterChanged();
		if (this.dateOption === compareOption) {
			return;
		}
		if (this.internalFilter.value[0]) {
			this.internalFilter.value = [this.getFromDate(this.internalFilter.value[0])];
		}
		switch (compareOption) {
			case 'BEFORE':
				this.internalFilter.operator = 'isBefore';
				break;
			case 'AFTER':
				this.internalFilter.operator = 'isAfter';
				break;
			default:
				this.internalFilter.operator = 'is';
				if (this.internalFilter.value[0]) {
					const fromDate = this.internalFilter.value[0];
					const toDate = dayjs(fromDate).add(1, 'days').toISOString();
					this.internalFilter.value = ['[' + fromDate + ' TO ' + toDate + ']'];
				}
		}
		this.dateOption = compareOption;
		this.filterSetService.updateFilter(this.filter, this.internalFilter);
	}

	setInternalFilter(filter: Filter) {
		const internalFilter = { ...filter };
		if (typeof internalFilter.value === 'string') {
			internalFilter.value = [internalFilter.value.toString()];
		}
		this.internalFilter = internalFilter;
	}

	determineOptions() {
		this.facetsLoading = true;

		if (this.filterConfig && this.filterConfig.type !== 'date') {
			switch (this.filterConfig.type) {
				case 'text': {
					this.internalFilter.operator = 'contains';
					break;
				}
				default: {
					this.filterSetService.getFacetOptions(this.filter, this.filterConfig).subscribe((options: BbFacetListOption[]) => {
						this.options = options;
						this.facetsLoading = false;
					});
					break;
				}
			}
		}
	}

	determineLabelForValue(value: string): string {
		if (this.filterConfig.type === 'facetquery') {
			// find configured label for this query
			const matchingOption = this.filterConfig.options.find(option => option.query === value);

			if (matchingOption) {
				return matchingOption.label;
			}
		} else if (this.filterConfig.type === 'boolean') {
			if (value === null) {
				value = 'empty';
			}
		}

		// default / fallback
		return this.valueLabelMap[value] || value;
	}

	setEditing(editing: boolean = true) {
		if (editing === false && this.filterSetService.getActiveFilter() !== this.filter) {
			return;
		}
		this.editing = editing;

		if (this.editing) {
			this.filterSetService.setActiveFilter(this.filter, this.filterConfig);
		}

		if (!this.editing && this.filterConfig.type === 'text') {
			this.filterSetService.setActiveFilter(null);
			this.doTextSearch();
		}
	}

	setDate() {
		this.date = new Date();
	}

	setDateOption(val: string) {
		this.dateOption = val;
	}

	getFromDate(dateString: string): string {
		if (dateString && dateString.indexOf('TO') !== -1) {
			dateString = dateString.replace('[', '');
			const splitter = dateString.split(' TO ');
			return splitter[0];
		} else if (typeof dateString === 'undefined') {
			return this.date.toISOString();
		}
		return dateString;
	}

	focusInput() {
		setTimeout(() => {
			if (this.facetList) {
				this.facetList.focusInput();
			}
			if (this.textSearchInput) {
				this.textSearchInput.nativeElement.focus();
			}
		});
	}

	removeFilter() {
		// call directly, why emit? No one listens except horizontal filter set and there the same call happens
		this.filterSetService.removeFilter(this.filter);
		// this.onRemove.emit(this.filter);
	}

	filterChanged() {
		if (this.filterConfig.type === 'date') {
			if (this.internalFilter.operator === 'is') {
				const fromDate = this.date.toISOString();
				const toDate = dayjs(fromDate).add(1, 'days').toISOString();
				this.internalFilter.value = ['[' + fromDate + ' TO ' + toDate + ']'];
			} else {
				this.internalFilter.value = [this.date.toISOString()];
			}
		}
		this.filterSetService.updateFilter(this.filter, this.internalFilter);
	}

	private doTextSearch(): void {
		if (this.internalFilter.value && this.internalFilter.value.length > 0
			&& this.internalFilter.value[0] === this.textSearchValue) {
			return;
		}

		if (this.textSearchValue) {
			this.internalFilter.value = [this.textSearchValue];
		} else {
			this.internalFilter.value = [];
		}
		this.filterSetService.updateFilter(this.filter, this.internalFilter);
		this.editing = false;
	}
}
